import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Delete02Icon,
  Exchange01Icon,
  UploadCircle01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { IDV_OVERALL_STATUS, IDV_OVERALL_STATUS_DISPLAY, MAX_RETRIES } from 'constants/idv';
import { useDisableCandidateFormActions } from 'contexts/contexts';
import { uploadSigned } from 'helpers/aws-s3';
import { selfieHelper } from 'helpers/candidate_application/pre-section-page-helpers';
import {
  extractFieldCodes,
  getBorderStatus,
  handleVerificationResponse,
  validateRetryCount,
  verifyIdDocument,
} from 'helpers/document-upload-helpers';
import evaluateFieldConditions from 'helpers/evaluate-field-conditions';
import { parseIdKey } from 'helpers/ids';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import { useVerifyIdDocumentByToken } from 'hooks/verify-id-document';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as mime from 'react-native-mime-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SelfieModal } from 'views/CandidateApp/CandidateForm/PreSubsectionFlows/components/Selfie/SelfieModal/SelfieModal';

import DisplayBox from 'components/DisplayBox/DisplayBox';
import { ExtractedFieldsDisplay } from 'components/ExtractedFieldsDisplay/ExtractedFieldsDisplay';
import { Button } from 'components/FormComponents/Button/Button';
import { FormMessage, MessageType } from 'components/FormComponents/FormMessage/FormMessage';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import { getCandidateApplication } from 'api/candidate-application-slice';

import styles from './DocumentUpload.module.scss';

export const DualUploadComponent = ({
  documentTitle = 'Select your files to upload',
  documentDescription = '',
  additionalInstructions = '',
  frontTitle = 'Front',
  backTitle = 'Back',
  uploadCode,
  selfieId,
  reTakeSelfie,
  frontValue,
  backValue,
  modelParams = {},
  listCode = '',
  metadata = {},
  className,
  bucketFolder = 'documents',
  maxSize = 20971520,
  caption = '',
  accept,
  brand,
  useModel = false,
  disabled = false,
  verifyDocument = false,
  rejected = false,
  expiryTolerance = null,
  skipVerification = false,
  needsComplementaryDoc = false,
  complementaryInstructions = '',
  displayFields = [],
  uploadError = '',
  uploadStatus = '',
  idValues = {},
  backConditions = null,
  onChange = () => {},
  getUpload = async () => {},
  signUpload = async () => {},
  createUpload = async () => {},
  deleteUpload = async () => true,
  uploadId = null,
  handleStateOnVerify = () => {},
  confirmedByCand = false,
}) => {
  const { token } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { verify: verifyByToken, isVerifying } = useVerifyIdDocumentByToken(token || undefined);
  const { disableFormActions, enableFormActions } = useDisableCandidateFormActions();

  const idUploadTranslations = t(`idUpload`, { ns: 'id_document_types', returnObjects: true });

  // Front upload state
  const [frontFileName, setFrontFileName] = useState();
  const [frontLoading, setFrontLoading] = useState(false);
  const [frontUploadId, setFrontUploadId] = useState(null);
  // Collapse extracted fields by default if already confirmed by candidate
  const [expand, setExpand] = useState(!confirmedByCand);

  // Back upload state
  const [backFileName, setBackFileName] = useState();
  const [backLoading, setBackLoading] = useState(false);
  const [backUploadId, setBackUploadId] = useState(null);
  const [backConditionsExists, setBackConditionsExists] = useState(
    Object.keys(backConditions || {}).length > 0 ? true : false,
  );

  // Front verification state (for conditional back upload)
  const [frontVerified, setFrontVerified] = useState(false);
  const [verifyingFront, setVerifyingFront] = useState(false);
  const frontVerificationAttemptedRef = useRef(false);

  // Shared verification state
  const [verifying, setVerifying] = useState(false);
  const [status, setStatus] = useState(uploadStatus);
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [error, setError] = useState(() => {
    if (!uploadError) return '';
    const failureKey =
      Array.isArray(uploadError) && uploadError.length > 0
        ? uploadError[0]
        : typeof uploadError === 'string'
          ? uploadError
          : null;
    return failureKey && idUploadTranslations?.reasonForFailure?.[failureKey]
      ? idUploadTranslations.reasonForFailure[failureKey]
      : failureKey || '';
  });
  const [verificationError, setVerificationError] = useState(null);
  const [errorType, setErrorType] = useState(MessageType.Error);
  const [isFileRejected, setIsFileRejected] = useState(rejected);

  const brandingStyleVariables = useBrandingStyleVariables(brand);

  // Track if verification has been attempted to prevent duplicate calls
  const verificationAttemptedRef = useRef(false);
  // Track if verification was successful to prevent re-verification
  // Initialize based on uploadStatus prop (for reload scenarios)
  const verificationSuccessfulRef = useRef(uploadStatus === IDV_OVERALL_STATUS.VERIFIED);
  // Track initial load to prevent verification on reload
  const isInitialLoadRef = useRef(true);
  // Track previous upload IDs to detect actual changes
  const prevUploadIdsRef = useRef({ front: null, back: null });
  // Track if uploads were deleted to reset retry count
  const wasDeletedRef = useRef(false);
  // Track attempt count directly (persists even when uploads are deleted)
  const attemptsRef = useRef(0);

  // Initialize attempts ref from metadata if available, otherwise keep existing value
  // Only update if metadata has a higher value (to preserve increments from local uploads)
  useEffect(() => {
    if (metadata?.attempts !== undefined) {
      // If metadata has attempts and it's higher than current, update
      // This handles cases where parent passes updated metadata from backend
      if (metadata.attempts > attemptsRef.current) {
        attemptsRef.current = metadata.attempts;
        setCurrentAttempts(metadata.attempts);
      }
      // If attemptsRef is 0 but metadata has a value, initialize it
      // This handles initial load scenarios
      else if (attemptsRef.current === 0 && metadata.attempts > 0) {
        attemptsRef.current = metadata.attempts;
        setCurrentAttempts(metadata.attempts);
      }
    }
  }, [metadata?.attempts]);

  // Get retry count - use the ref value (which persists across uploads/deletions)
  // Note: This is a function to ensure we always get the current ref value
  const getRetryCount = () => attemptsRef.current;

  // Check if both uploads are complete
  const bothUploadsComplete = useMemo(() => {
    return frontUploadId !== null && backUploadId !== null;
  }, [frontUploadId, backUploadId]);

  // Track upload changes and mark initial load as complete
  useEffect(() => {
    // Check if uploads have actually changed (not just initial load)
    const frontChanged = prevUploadIdsRef.current.front !== frontUploadId;
    const backChanged = prevUploadIdsRef.current.back !== backUploadId;
    const hasChanged = frontChanged || backChanged;

    // After initial load, mark as complete
    if (isInitialLoadRef.current && frontUploadId !== null && backUploadId !== null) {
      isInitialLoadRef.current = false;
      // If already verified on load, don't verify again
      if (uploadStatus === IDV_OVERALL_STATUS.VERIFIED) {
        verificationSuccessfulRef.current = true;
      }
    }

    // Only reset flags if uploads actually changed (new upload, not just load)
    if (hasChanged && !isInitialLoadRef.current) {
      verificationAttemptedRef.current = false;
      // Only reset successful flag if uploads are not already verified
      if (uploadStatus !== IDV_OVERALL_STATUS.VERIFIED) {
        verificationSuccessfulRef.current = false;
      }
    }

    // Update previous IDs
    prevUploadIdsRef.current = { front: frontUploadId, back: backUploadId };
  }, [frontUploadId, backUploadId, uploadStatus]);

  // Set successful flag if status is already verified (e.g., on initial load)
  useEffect(() => {
    if (status === IDV_OVERALL_STATUS.VERIFIED || uploadStatus === IDV_OVERALL_STATUS.VERIFIED) {
      verificationSuccessfulRef.current = true;
      // If backConditionsExists is true and front is uploaded, mark front as verified
      if (backConditionsExists && frontUploadId) {
        setFrontVerified(true);
      }
    }
  }, [status, uploadStatus, backConditionsExists, frontUploadId]);

  // Track attempts in state so shouldHideUploads can react to changes
  const [currentAttempts, setCurrentAttempts] = useState(0);

  // Update currentAttempts when attemptsRef changes
  useEffect(() => {
    setCurrentAttempts(attemptsRef.current);
  }, [metadata?.attempts]); // Update when metadata changes (which reflects attemptsRef updates)

  // Check if we should hide upload boxes (max retries reached)
  const shouldHideUploads = useMemo(() => {
    const attempts = getRetryCount();
    return (
      attempts >= MAX_RETRIES &&
      (uploadStatus === IDV_OVERALL_STATUS.FAILED ||
        uploadStatus === IDV_OVERALL_STATUS.REJECTED ||
        uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW)
    );
  }, [uploadStatus, currentAttempts]); // Include currentAttempts so it updates when attemptsRef changes

  // Initialize state from props
  // Track initialized values to avoid re-initialization with same values
  const initializedValuesRef = useRef({ front: null, back: null });

  useEffect(() => {
    const initializeUpload = async (value, setFileName, setUploadId, side) => {
      const valueId = useModel ? value?.id : value;

      // Skip if already initialized with this value (using loose equality to handle string/number mismatch)
      // eslint-disable-next-line eqeqeq
      if (
        initializedValuesRef.current[side] == valueId &&
        initializedValuesRef.current[side] !== null
      ) {
        return;
      }

      // Update the initialized value ref
      initializedValuesRef.current[side] = valueId;

      if (!value) {
        setFileName(null);
        setUploadId(null);
        return;
      }

      if (useModel) {
        setFileName(value.file_name);
        setUploadId(value.id);
      } else {
        try {
          const upload = await getUpload(value);
          if (upload) {
            setFileName(upload.file_name);
            setUploadId(upload.id);
          } else {
            console.warn(`[DualUpload] getUpload returned null for ${side} value:`, value);
            // Don't set to null if we had a value - the upload might still exist
            // Only set to null if the upload truly doesn't exist
          }
        } catch (error) {
          console.error(`[DualUpload] Error getting ${side} upload:`, error);
          // Don't set to null on error - might be a transient network issue
          // The value prop still has the ID, so the upload should exist
        }
      }
    };

    // Run both initializations
    initializeUpload(frontValue, setFrontFileName, setFrontUploadId, 'front');
    initializeUpload(backValue, setBackFileName, setBackUploadId, 'back');
  }, [frontValue, backValue, useModel, getUpload]);

  // Trigger front-only verification when backConditionsExists is true
  useEffect(() => {
    const triggerFrontVerification = async () => {
      // Only verify front if backConditionsExists is true
      if (!backConditionsExists) {
        return;
      }

      // Don't verify on initial load
      if (isInitialLoadRef.current) {
        return;
      }

      if (
        !verifyDocument ||
        !token ||
        !frontUploadId ||
        verifyingFront ||
        frontVerificationAttemptedRef.current ||
        frontVerified
      ) {
        return;
      }

      // Only verify if we haven't already verified
      if (status === IDV_OVERALL_STATUS.VERIFIED || uploadStatus === IDV_OVERALL_STATUS.VERIFIED) {
        setFrontVerified(true);
        return;
      }

      const idCode = metadata?.id_type || null;
      if (!idCode) {
        return;
      }

      frontVerificationAttemptedRef.current = true;
      setVerifyingFront(true);
      setError(null);
      setVerificationError(null);

      try {
        const result = await verifyIdDocument(
          {
            uploadId: frontUploadId,
            metadata,
            listCode,
            expiryTolerance,
            verifyByToken,
            reTakeSelfie,
            getUpload,
            useModel,
            value: useModel ? frontValue : frontUploadId,
            skipVerification,
          },
          {
            setVerifying: setVerifyingFront,
            setStatus,
            setError,
            setErrorType,
            setVerificationError,
            setShowSelfieModal,
            onChange: () => {
              // Call onChange with front upload ID only
              if (useModel) {
                onChange({ front: frontValue, back: backValue || null });
              } else {
                onChange({ front: frontUploadId, back: backUploadId || null });
              }
            },
            setIsFileRejected,
            validateRetryCount: (upload, callbacks, useModel, status) =>
              validateRetryCount(
                upload,
                { setStatus, setError, setErrorType, onChange, setIsFileRejected },
                useModel,
                status,
              ),
          },
        );

        if (result.success) {
          setIsFileRejected(false);
          setFrontVerified(true);
          // Call onChange on success
          if (useModel) {
            onChange({ front: frontValue, back: backValue || null });
          } else {
            onChange({ front: frontUploadId, back: backUploadId || null });
          }
        } else {
          // Verification failed - allow retry
          frontVerificationAttemptedRef.current = false;
        }
      } catch (error) {
        console.error('Error verifying front document:', error);
        if (setVerificationError) {
          setVerificationError(error?.message || 'Verification failed');
        }

        // Get upload for retry validation
        const upload = useModel ? frontValue : await getUpload(frontUploadId);
        validateRetryCount(
          upload,
          { setStatus, setError, setErrorType, onChange, setIsFileRejected },
          useModel,
          null,
        );

        // On verification error, allow retry
        frontVerificationAttemptedRef.current = false;
      } finally {
        setVerifyingFront(false);
        handleStateOnVerify(false);
        enableFormActions();
      }
    };

    if (backConditionsExists && frontUploadId) {
      triggerFrontVerification();
    }
  }, [
    backConditionsExists,
    frontUploadId,
    verifyDocument,
    token,
    metadata,
    listCode,
    expiryTolerance,
    verifyByToken,
    reTakeSelfie,
    getUpload,
    useModel,
    frontValue,
    uploadStatus,
    status,
  ]);

  // Trigger verification when both uploads are complete
  useEffect(() => {
    const triggerVerification = async () => {
      // Don't verify on initial load - only verify when uploads are actually uploaded/updated
      if (isInitialLoadRef.current) {
        return;
      }

      // If backConditionsExists is true, front must be verified first
      if (backConditionsExists && !frontVerified) {
        return;
      }

      if (
        !verifyDocument ||
        !token ||
        !bothUploadsComplete ||
        !frontUploadId ||
        !backUploadId ||
        verifying ||
        verificationAttemptedRef.current ||
        verificationSuccessfulRef.current
      ) {
        return;
      }

      // Only verify if we haven't already verified (check both status and uploadStatus)
      if (status === IDV_OVERALL_STATUS.VERIFIED || uploadStatus === IDV_OVERALL_STATUS.VERIFIED) {
        verificationSuccessfulRef.current = true;
        return;
      }

      const idCode = metadata?.id_type || null;
      if (!idCode) {
        return;
      }

      verificationAttemptedRef.current = true;
      setVerifying(true);
      setError(null);
      setVerificationError(null);

      try {
        const { idType, countryCode } = parseIdKey(idCode);

        // Call verifyByToken with backUploadId
        const data = await verifyByToken({
          listCode,
          uploadId: frontUploadId,
          backUploadId: backUploadId,
          idCode,
          idType,
          idCountry: countryCode || null,
          expiryTolerance,
        });

        // Use handleVerificationResponse to process the result
        // It will get the upload itself to check metadata.attempts (source of truth)
        const result = await handleVerificationResponse(
          data,
          {
            verifyByToken,
            getUpload,
            metadata,
            listCode,
            expiryTolerance,
            reTakeSelfie,
            useModel,
            value: useModel ? frontValue : frontUploadId,
            uploadId: frontUploadId,
          },
          {
            setStatus,
            setError,
            setErrorType,
            setVerificationError,
            setShowSelfieModal,
            onChange: () => {
              // Call onChange with both upload IDs
              if (useModel) {
                onChange({ front: frontValue, back: backValue });
              } else {
                onChange({ front: frontUploadId, back: backUploadId });
              }
            },
            setIsFileRejected,
            reTakeSelfie,
            validateRetryCount: (upload, callbacks, useModel, status) =>
              validateRetryCount(
                upload,
                { setStatus, setError, setErrorType, onChange, setIsFileRejected },
                useModel,
                status,
              ),
          },
        );

        if (result.success) {
          setIsFileRejected(false);
          verificationSuccessfulRef.current = true; // Mark as successful to prevent re-verification
          // Call onChange on success
          if (useModel) {
            onChange({ front: frontValue, back: backValue });
          } else {
            onChange({ front: frontUploadId, back: backUploadId });
          }
        } else {
          // Verification failed - don't delete uploads, let user see the error and retry
          // The status and error are already set by handleVerificationResponse
          verificationAttemptedRef.current = false; // Allow retry
        }
      } catch (error) {
        console.error('Error verifying dual document:', error);
        if (setVerificationError) {
          setVerificationError(error?.message || 'Verification failed');
        }

        // Get upload for retry validation
        const upload = useModel ? frontValue : await getUpload(frontUploadId);
        // In catch block, we don't have a status from the response, so pass null
        // validateRetryCount won't show the "accepted" message if status is null
        validateRetryCount(
          upload,
          { setStatus, setError, setErrorType, onChange, setIsFileRejected },
          useModel,
          null,
        );

        // On verification error, don't delete uploads - let user see the error and retry
        verificationAttemptedRef.current = false; // Allow retry
      } finally {
        setVerifying(false);
        handleStateOnVerify(false);
        enableFormActions();
      }
    };

    bothUploadsComplete && triggerVerification();
  }, [
    bothUploadsComplete,
    frontUploadId,
    backUploadId,
    verifyDocument,
    token,
    backConditionsExists,
    frontVerified,
  ]);

  const handleFileUpload = async (file, isFront) => {
    disableFormActions();
    const setLoading = isFront ? setFrontLoading : setBackLoading;
    const setFileName = isFront ? setFrontFileName : setBackFileName;
    const setUploadId = isFront ? setFrontUploadId : setBackUploadId;
    const currentValue = isFront ? frontValue : backValue;
    const currentUploadId = isFront ? frontUploadId : backUploadId;

    setLoading(true);
    handleStateOnVerify(true);
    setError(null);
    setVerificationError(null);

    const prevUploadId = useModel ? currentValue?.id : currentValue;

    try {
      // Sign upload
      const params = {
        file_name: file.name,
        file_type: file.type,
        file_ext: mime.extension(file.type),
        bucket_folder: bucketFolder,
      };

      const signedFile = await signUpload(params);

      // Upload to S3
      await uploadSigned(signedFile.url, file, file.type);

      // For dual uploads, both uploads should have the same attempt count
      // - If no previous metadata exists (getRetryCount is 0), default to 1
      // - If previous metadata exists, increment by 1, but only if it's a front upload
      // - For back upload, use the same attempts as the front upload
      let attempts;
      if (isFront) {
        // Front upload: increment the attempts ref
        attemptsRef.current = attemptsRef.current === 0 ? 1 : attemptsRef.current + 1;
        attempts = attemptsRef.current;
        // Update state so shouldHideUploads can react
        setCurrentAttempts(attemptsRef.current);
      } else {
        // Back upload: use the same attempts as front upload (from ref)
        attempts = attemptsRef.current || 1;
        // Update state so shouldHideUploads can react
        setCurrentAttempts(attemptsRef.current || 1);
      }

      const updatedMetadata = {
        ...metadata,
        attempts,
        dual: isFront ? 'front' : 'back',
      };

      // Save model
      const uploadData = {
        ...modelParams,
        metadata: updatedMetadata,
        file_name: signedFile.file_name,
        file_type: signedFile.file_type,
        hash_key: signedFile.key,
      };

      const upload = await createUpload(uploadData);

      // When retrying (uploading a new file), delete both old uploads (front and back)
      // This matches DocumentUpload behavior - delete old uploads when user retries
      // For dual uploads, when user retries by uploading one side, delete both old uploads
      if (prevUploadId && prevUploadId !== upload.id) {
        // User is retrying - delete both old uploads (front and back)
        const oldFrontUploadId = isFront ? frontUploadId : frontValue;
        const oldBackUploadId = isFront ? backUploadId : backValue;

        // Delete old front upload if it exists and clear its state if we're replacing back
        if (oldFrontUploadId) {
          try {
            await deleteUpload(oldFrontUploadId);
            // If we're replacing the back, clear front state so verification doesn't trigger with old front ID
            if (!isFront) {
              setFrontFileName(null);
              setFrontUploadId(null);
              // Reset verifying state so front upload button isn't disabled
              setVerifying(false);
            }
          } catch (e) {
            console.error('Failed to delete previous front upload:', e);
          }
        }

        // Delete old back upload if it exists and clear its state if we're replacing front
        if (oldBackUploadId) {
          try {
            await deleteUpload(oldBackUploadId);
            // If we're replacing the front, clear back state so verification doesn't trigger with old back ID
            if (isFront) {
              setBackFileName(null);
              setBackUploadId(null);
              // Reset verifying state so back upload button isn't disabled
              setVerifying(false);
            }
          } catch (e) {
            console.error('Failed to delete previous back upload:', e);
          }
        }
      }

      // Reset verification attempt flags when a new upload is made
      verificationAttemptedRef.current = false;
      verificationSuccessfulRef.current = false;
      // Reset verifying state when replacing (in case it was still true from previous verification)
      if (prevUploadId && prevUploadId !== upload.id) {
        setVerifying(false);
      }
      // Reset front verification if front is replaced
      if (isFront) {
        setFrontVerified(false);
        frontVerificationAttemptedRef.current = false;
      }
      // Mark that initial load is complete (so verification can run)
      isInitialLoadRef.current = false;

      // Update the appropriate value (front or back)
      // If we deleted the old back upload, pass null for back
      if (isFront) {
        const backToPass =
          prevUploadId && prevUploadId !== upload.id ? null : backValue || backUploadId;
        onChange({
          front: useModel ? upload : upload.id,
          back: backToPass,
        });
      } else {
        const frontToPass =
          prevUploadId && prevUploadId !== upload.id ? null : frontValue || frontUploadId;
        onChange({
          front: frontToPass,
          back: useModel ? upload : upload.id,
        });
      }
      setFileName(upload.file_name);
      setUploadId(upload.id);
      setIsFileRejected(false);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileDelete = async (id, isFront) => {
    if (id) {
      const setLoading = isFront ? setFrontLoading : setBackLoading;
      setLoading(true);
      try {
        const deleteId = parseInt(id);
        // Extract codes - function handles empty/undefined displayFields safely
        const extractedCodes = extractFieldCodes(displayFields);
        const fieldCodes = extractedCodes.length > 0 ? extractedCodes : null;
        // Pass field codes so we can directly remove associated field codes from candidateValues
        await deleteUpload(deleteId, fieldCodes);
      } catch (error) {
        console.error('[DUAL UPLOAD] deleteUpload failed:', error);
      }
      setLoading(false);
    }

    const setFileName = isFront ? setFrontFileName : setBackFileName;
    const setUploadId = isFront ? setFrontUploadId : setBackUploadId;

    setFileName(null);
    setUploadId(null);

    // Update onChange to remove the deleted upload
    if (isFront) {
      onChange({
        front: null,
        back: backValue || backUploadId,
      });
    } else {
      onChange({
        front: frontValue || frontUploadId,
        back: null,
      });
    }

    // Reset verification flags when deleting
    verificationAttemptedRef.current = false;
    verificationSuccessfulRef.current = false;
    // Reset initial load flag so verification can run again when both are uploaded
    isInitialLoadRef.current = false;
    // Mark that uploads were deleted to reset retry count on next upload
    wasDeletedRef.current = true;
    // Reset front verification if front is deleted
    if (isFront) {
      setFrontVerified(false);
      frontVerificationAttemptedRef.current = false;
    }

    // Clear errors and status when deleting (always clear, regardless of whether both are deleted)
    setStatus(null);
    setError(null);
    setVerificationError(null);
    setIsFileRejected(false);
  };

  const handleFileInput = (event, isFront) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > maxSize) {
        setError('File is too large. Max size is 10MB. Please upload a smaller file.');
        setVerificationError(null);
        setErrorType(MessageType.Error);
        event.target.value = '';
        return;
      }
      if (file.type === 'image/heic') {
        setError('HEIC files are not supported. Please upload a JPEG/JPG/PNG/PDF file.');
        setVerificationError(null);
        setErrorType(MessageType.Error);
        event.target.value = '';
        return;
      }
      handleFileUpload(file, isFront);
    }
  };

  const handleReplace = isFront => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = event => handleFileInput(event, isFront);
    input.click();
  };

  const uploadSelfie = useCallback(
    async photo => {
      // Upload new selfie (this will trigger handleSelfieUpload in backend)
      await selfieHelper({
        photo,
        candidate: { id: uploadCode },
        signUpload,
        createUpload,
      });

      // Delete old selfie first (if exists)
      if (selfieId) {
        await deleteUpload(selfieId);
      }

      // After selfie upload, refetch application data to get updated id_upload_details
      // The backend will have recalculated face similarity for all affected ID uploads
      if (token) {
        await dispatch(getCandidateApplication(token));
      }

      setShowSelfieModal(false);
    },
    [token, uploadCode, signUpload, createUpload, selfieId, deleteUpload, dispatch],
  );

  const borderStatus = useMemo(() => getBorderStatus(status), [status]);

  const backConditionsMet = useMemo(() => {
    if (!backConditionsExists) return true;
    return evaluateFieldConditions(backConditions, idValues, {});
  }, [backConditionsExists, idValues]);

  // Render individual upload box
  const renderUploadBox = (isFront, fileName, loading, uploadId, value) => {
    const title = isFront ? frontTitle : backTitle;
    const currentValue = value || uploadId;

    if (shouldHideUploads) {
      return null;
    }

    return (
      <div
        className={cn(
          'u-padding surface-white u-border u-border-radius--medium u-drop-shadow--xs d-flex flex-column u-gap-medium',
          loading ? 'u-border-in-progress' : fileName ? borderStatus : '',
        )}>
        <div className="d-flex justify-content-between align-items-center u-gap-medium flex-wrap">
          <div className="d-flex flex-column u-gap u-max-upload-card-width">
            {caption && (
              <span className="tertiary-text u-font-size-small u-font-weight-500">{caption}</span>
            )}
            <div className="d-flex flex-column u-gap-x-small">
              <span className="u-font-size-large u-font-weight-500">{`${documentTitle} - ${title}`}</span>
              {documentDescription && (
                <span className="secondary-text u-font-size-x-small u-font-weight-400">
                  {`${documentDescription} - ${title}`}
                </span>
              )}
              {additionalInstructions && (
                <span className="secondary-text u-font-size-x-small u-font-weight-400">
                  {additionalInstructions}
                </span>
              )}
            </div>
            {bothUploadsComplete && (
              <div className="d-flex align-items-center u-gap">
                {isFileRejected ? (
                  <StatusPill text="Rejected" status="error" />
                ) : (
                  <>
                    {status === IDV_OVERALL_STATUS.FAILED &&
                      getRetryCount() < MAX_RETRIES &&
                      uploadStatus === IDV_OVERALL_STATUS.FAILED && (
                        <StatusPill
                          text={IDV_OVERALL_STATUS_DISPLAY[status].title}
                          status={IDV_OVERALL_STATUS_DISPLAY[status].color}
                        />
                      )}
                    {status !== IDV_OVERALL_STATUS.FAILED &&
                      status !== IDV_OVERALL_STATUS.REJECTED &&
                      !error && (
                        <>
                          <StatusPill
                            text={
                              loading ? 'Uploading...' : fileName ? 'Uploaded' : 'Not Yet Uploaded'
                            }
                            status={loading ? 'warning' : fileName ? 'success' : 'neutral'}
                          />
                          {status && status === IDV_OVERALL_STATUS.VERIFIED && (
                            <StatusPill text="Verified" status="success" />
                          )}
                        </>
                      )}
                  </>
                )}
              </div>
            )}
          </div>

          {fileName ? (
            !disabled &&
            getRetryCount() < MAX_RETRIES && (
              <div className="d-flex align-items-center u-gap">
                <Button
                  type="secondary"
                  edged
                  onClick={() => handleReplace(isFront)}
                  disabled={loading || verifying || verifyingFront}
                  style={brandingStyleVariables}>
                  <HugeiconsIcon
                    size="20"
                    icon={Exchange01Icon}
                    strokeWidth="1.5"
                    className="u-margin-right--small"
                  />
                  <span>Replace</span>
                </Button>
                <IconComponent
                  icon={Delete02Icon}
                  onClick={() => handleFileDelete(currentValue, isFront)}
                  tip="Delete"
                  type="error"
                  size="small"
                  noBackground
                />
              </div>
            )
          ) : (
            <label
              className={cn(styles.uploadButton, 'u-border', {
                'u-disabled': loading || verifying || verifyingFront || disabled,
              })}
              style={{
                '--brand-button-color': brandingStyleVariables['--button-color'],
                '--brand-button-text-color': brandingStyleVariables['--button-text-color'],
              }}>
              <input
                type="file"
                accept={accept}
                onChange={event => handleFileInput(event, isFront)}
                disabled={loading || verifying || verifyingFront || disabled}
                style={{ display: 'none' }}
              />
              <HugeiconsIcon size="20" icon={UploadCircle01Icon} strokeWidth="1.5" />
              <span className="u-font-size-small u-font-weight-400 primary-text">
                {loading ? 'Uploading...' : 'Upload'}
              </span>
            </label>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <SelfieModal
        show={showSelfieModal}
        brand={brand}
        screenName="camera"
        noClose
        title="Retake selfie"
        description="We ran into a little issue verifying your ID with the photo you provided. Could you take a clearer picture for us? Make sure your face is fully visible and well-lit."
        onSave={photo => uploadSelfie(photo)}
      />
      <div
        className={cn(
          styles.root,
          { [className]: className, 'u-disabled': disabled },
          'd-flex flex-column u-gap-medium u-padding u-border u-border-radius--medium',
        )}
        style={brandingStyleVariables}>
        <div className="d-flex flex-column u-gap-medium">
          <div className="d-flex justify-content-between align-items-center u-gap-medium flex-wrap">
            <span className="u-font-size-large u-font-weight-500">{documentTitle}</span>
            {!frontLoading &&
              !backLoading &&
              !verifying &&
              !verifyingFront &&
              displayFields &&
              displayFields.length > 0 &&
              frontFileName &&
              backFileName &&
              !disabled &&
              (uploadStatus === IDV_OVERALL_STATUS.VERIFIED ||
                uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
                (uploadStatus === IDV_OVERALL_STATUS.FAILED && getRetryCount() >= MAX_RETRIES)) && (
                <IconComponent
                  icon={expand ? ArrowUp01Icon : ArrowDown01Icon}
                  noBackground
                  type="secondary"
                  size="medium"
                  strokeWidth={1.5}
                  onClick={() => setExpand(!expand)}
                />
              )}
          </div>
          {renderUploadBox(true, frontFileName, frontLoading, frontUploadId, frontValue)}
          {(!backConditionsExists || (backConditionsMet && frontVerified)) &&
            renderUploadBox(false, backFileName, backLoading, backUploadId, backValue)}
        </div>

        {needsComplementaryDoc && complementaryInstructions && (
          <DisplayBox show={true} alignLeft>
            <span className="u-font-size-small u-font-weight-400">{complementaryInstructions}</span>
          </DisplayBox>
        )}

        {/* Show error messages - error from validateRetryCount (accepted message) or other errors */}
        {error && <FormMessage message={error} type={errorType} noMargin closeable={false} />}

        {/* Show extracted fields when verification is successful, needs review, or failed at max retries */}
        {!frontLoading &&
          !backLoading &&
          !verifying &&
          !verifyingFront &&
          displayFields &&
          displayFields.length > 0 &&
          frontFileName &&
          backFileName &&
          expand &&
          (uploadStatus === IDV_OVERALL_STATUS.VERIFIED ||
            uploadStatus === IDV_OVERALL_STATUS.NEEDS_REVIEW ||
            (uploadStatus === IDV_OVERALL_STATUS.FAILED && getRetryCount() >= MAX_RETRIES)) && (
            <div className="u-padding u-border u-border-radius--medium">
              <ExtractedFieldsDisplay
                displayFields={displayFields}
                idValues={idValues}
                brand={brand}
                uploadId={uploadId || frontUploadId}
                confirmedByCand={confirmedByCand}
                onConfirm={() => setExpand(false)}
              />
            </div>
          )}

        {/* Show verification status when verifying */}
        {(verifying || verifyingFront) && (
          <FormMessage
            message={verifyingFront ? 'Verifying front document...' : 'Verifying documents...'}
            type="Info"
            noMargin
            closeable={false}
          />
        )}
      </div>
    </>
  );
};

DualUploadComponent.propTypes = {
  documentTitle: PropTypes.string,
  frontTitle: PropTypes.string,
  backTitle: PropTypes.string,
  frontValue: PropTypes.any,
  backValue: PropTypes.any,
  modelParams: PropTypes.object,
  metadata: PropTypes.object,
  className: PropTypes.string,
  bucketFolder: PropTypes.string,
  maxSize: PropTypes.number,
  accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  brand: PropTypes.object,
  useModel: PropTypes.bool,
  verifyDocument: PropTypes.bool,
  skipVerification: PropTypes.bool,
  onChange: PropTypes.func,
  getUpload: PropTypes.func,
  signUpload: PropTypes.func,
  createUpload: PropTypes.func,
  deleteUpload: PropTypes.func,
  displayFields: PropTypes.array,
  idValues: PropTypes.object,
  uploadId: PropTypes.number,
  confirmedByCand: PropTypes.bool,
};
